// @NOTE: Default share fallback image is here -> '/images/share/default.png',

import { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import PropTypes from "prop-types";

import { useAnalytics, useImageUrl, useOutsideClick } from "@hmn/coolinarika-web-core/hooks";

import { Button, buttonSizes, buttonVariants } from "@hmn/coolinarika-web-ui/components/Button";
import { withErrorBoundary } from "@hmn/coolinarika-web-ui/components/ErrorBoundary";
import { CloseIcon, Icon, ShareIcon } from "@hmn/coolinarika-web-ui/components/Icon";
import {
    ShareButton,
    shareButtonNetworks,
} from "@hmn/coolinarika-web-ui/components/Share/components";

import styles, { motionVariants } from "./Share.style";

const isDev = process.env.NODE_ENV !== "production";
const webRoot = process.env.NEXT_PUBLIC_WEB_ROOT;

const ShareStyled = styled.div((props) => ({ ...styles(props) }));

const isStandaloneApp = () => {
    const isIosStandalone = window.navigator.standalone;
    const isAndroidStandalone = window.matchMedia("(display-mode: standalone)").matches;
    return isIosStandalone || isAndroidStandalone;
};

const Share = ({
    shareCtaLabel,
    url,
    text,
    media,
    mediaImageId,
    disabled,
    position,
    className,
    pwaOnly,
    ...rest
}) => {
    const shareRef = useRef();

    const [mounted, setMounted] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);

    const shareMediaImageUrl = useImageUrl(mediaImageId, "2:3", 1000, undefined, "jpg");

    // Close actions on outside click
    useOutsideClick(shareRef, () => setShareOpen(false));

    const webBase = `${isDev ? "http" : "https"}://${webRoot}`;

    useEffect(() => {
        setMounted(true);
    }, []);
    const { eventWithTrackingData } = useAnalytics("coolinarika.share", {
        trackingData: {
            elementCategory: "social",
        },
    });

    if (pwaOnly && typeof window !== "undefined") {
        if (!isStandaloneApp()) {
            return null;
        }
    }

    if (disabled || !url || !mounted) {
        return null;
    }

    // Get pinterest share media asset
    const getPinterestShareMedia =
        media || shareMediaImageUrl || `${webBase}/images/share/default.png`;

    const handleClick = async () => {
        if (pwaOnly && navigator.share) {
            let mediaArray;

            // We have to send a request because files HAS to have actual files... and cors will block us on localhost
            try {
                const blob = await fetch(getPinterestShareMedia).then((response) =>
                    response.blob(),
                );
                const file = new File([blob], getPinterestShareMedia, { type: blob.type });
                const fileArray = [file];
                if (navigator.canShare?.({ files: fileArray })) {
                    mediaArray = fileArray;
                }
            } catch (e) {
                // Getting picture failed - keep it quiet
                // console.log(e)
            }

            navigator
                .share({
                    files: mediaArray,
                    title: text,
                    text,
                    url,
                })
                .then(() => {
                    eventWithTrackingData({
                        elementLabel: "PWA",
                    });
                    // console.log('Successful share')
                })
                .catch((error) => {
                    //  console.log('Error sharing', error)
                });
        } else {
            setShareOpen(!shareOpen);
        }
    };

    return (
        <ShareStyled shareOpen={shareOpen} position={position} className={className} {...rest}>
            <div className="share_inner" ref={shareRef}>
                <motion.div
                    className="share_cta"
                    initial="visibleCta"
                    animate={shareOpen ? "hiddenCta" : "visibleCta"}
                    variants={motionVariants()}
                >
                    <Button
                        className="share_ctaBtn"
                        title={shareCtaLabel}
                        variant={buttonVariants.SECONDARY}
                        size={buttonSizes.NORMAL}
                        isIconOnly={!shareCtaLabel}
                        iconLeftComponent={<Icon icon={ShareIcon} />}
                        onClick={handleClick}
                    >
                        {shareCtaLabel}
                    </Button>
                </motion.div>
                <motion.div
                    className="share_buttons"
                    initial="closed"
                    animate={shareOpen ? "open" : "closed"}
                    variants={motionVariants()}
                >
                    <ul className="share_buttonsList">
                        <li>
                            <ShareButton
                                network={shareButtonNetworks.PINTEREST}
                                url={url}
                                text={text}
                                media={getPinterestShareMedia}
                                windowWidth={1000}
                                windowHeight={730}
                                onClick={() =>
                                    eventWithTrackingData({
                                        elementLabel: "Pinterest",
                                    })
                                }
                            />
                        </li>
                        <li>
                            <ShareButton
                                network={shareButtonNetworks.FACEBOOK}
                                url={url}
                                windowHeight={740}
                                onClick={() =>
                                    eventWithTrackingData({
                                        elementLabel: "Facebook",
                                    })
                                }
                            />
                        </li>
                        <li>
                            <Button
                                className="share_closeBtn"
                                variant={buttonVariants.TERTIARY}
                                size={buttonSizes.SMALL}
                                title="Zatvori"
                                onClick={() => setShareOpen(false)}
                                isIconOnly
                            >
                                <Icon icon={CloseIcon} size={30} />
                            </Button>
                        </li>
                    </ul>
                </motion.div>
            </div>
        </ShareStyled>
    );
};

const sharePosition = Object.freeze({
    TOP_RIGHT_HERO: "topRightHero",
});

Share.propTypes = {
    shareCtaLabel: PropTypes.string,
    url: PropTypes.string,
    text: PropTypes.string,
    media: PropTypes.string,
    mediaImageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    disabled: PropTypes.bool,
    position: PropTypes.oneOf([...Object.values(sharePosition)]),
    className: PropTypes.string,
    pwaOnly: PropTypes.bool,
};

Share.defaultProps = {
    shareCtaLabel: "Podijeli",
    url: undefined,
    text: undefined,
    media: undefined,
    mediaImageId: undefined,
    disabled: false,
    position: undefined,
    className: undefined,
    pwaOnly: undefined,
};

export { sharePosition };

export default withErrorBoundary(Share);
